import {
  Card,
  CardContent,
  Typography,
  Stack,
  Button,
  Box,
  FilterOptionsState,
} from '@mui/material'
import { Add } from '@mui/icons-material'
import { AutocompleteInput } from '@/components/inputs'
import { formatTaxId, sanitizeCnpj } from '@/helpers'
import { useListBorrowers, useReadBorrower } from '@/api/accounting-dealer'
import { useCompany } from '@/context/companies'
import { useEffect, useState } from 'react'
import { Borrower as TBorrower } from '@/types'
import { useDebounce } from '@/hooks'
import { BorrowerRegistrationDialog } from '../borrower-registration-dialog'
import { BorrowerDetailsCard } from '../borrower-details-card/borrower-details-card'
import { useFormContext } from 'react-hook-form'
import { FiscalNoteFormData } from '../../schema'
import { keepPreviousData, useQueryClient } from '@tanstack/react-query'

export function BorrowerSelectionCard() {
  const { watch, control, setValue } = useFormContext<FiscalNoteFormData>()

  const queryClient = useQueryClient()

  const { company } = useCompany()
  const [searchCustomer, setSearchCustomer] = useState('')
  const [
    isBorrowerRegistrationDialogOpen,
    setIsBorrowerRegistrationDialogOpen,
  ] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)

  const [borrowersCache, setBorrowersCache] = useState<TBorrower[]>([])
  const [apiSearch, setApiSearch] = useState<BorrowerApiSearch>({})
  const [isInitialFetchDone, setIsInitialFetchDone] = useState(false)

  const debouncedSearchCustomer = useDebounce(searchCustomer, 300)

  const borrowerKey = watch('borrower.key')
  const hasApiSearch = Boolean(apiSearch.tax_id || apiSearch.name)

  const { data: fetchedBorrowers, isLoading } = useListBorrowers(
    {
      companyKey: company.key,
      params: apiSearch,
    },
    {
      enabled: !!company.key && (!isInitialFetchDone || hasApiSearch),
    },
  )

  useEffect(() => {
    if (fetchedBorrowers) {
      setBorrowersCache((prevCache) => {
        const cacheMap = new Map(prevCache.map((item) => [item.key, item]))
        fetchedBorrowers.forEach((item) => {
          cacheMap.set(item.key, item)
        })
        return Array.from(cacheMap.values())
      })
      if (!isInitialFetchDone) {
        setIsInitialFetchDone(true)
      }
    }
  }, [fetchedBorrowers, isInitialFetchDone])

  useEffect(() => {
    const nextSearch = resolveBorrowerApiSearch(
      debouncedSearchCustomer,
      borrowersCache,
    )
    setApiSearch((current) =>
      isSameBorrowerApiSearch(current, nextSearch) ? current : nextSearch,
    )
  }, [debouncedSearchCustomer, borrowersCache])

  const { data: borrower, isFetching: isReadingBorrower } = useReadBorrower(
    {
      companyKey: company.key,
      params: { borrower_key: borrowerKey },
    },
    { enabled: !!borrowerKey, placeholderData: keepPreviousData },
  )

  const onSearchCustomerChange = (newInputValue: string) => {
    setSearchCustomer(newInputValue)
  }

  useEffect(() => {
    if (borrower) {
      setValue(
        'borrower',
        {
          key: borrower.key,
          name: borrower.name,
          tax_id: borrower.tax_id,
          tax_id_type: borrower.tax_id_type,
        },
        { shouldValidate: true },
      )
    }
  }, [borrower, setValue])

  const getBorrowerOptions = (borrowers: TBorrower[] | undefined) => {
    return borrowers ?? []
  }

  const handleBorrowerRegistered = (borrower: TBorrower) => {
    setValue('borrower', borrower)
    setBorrowersCache((prev) => [...prev, borrower])
    setIsBorrowerRegistrationDialogOpen(false)
  }

  const handleBorrowerUpdated = (borrower: TBorrower) => {
    queryClient.invalidateQueries({
      queryKey: [
        'borrower',
        {
          companyKey: company.key,
          params: { borrower_key: borrower.key },
        },
      ],
    })
    setIsEditDialogOpen(false)
  }

  const handleEditBorrower = () => {
    setIsEditDialogOpen(true)
  }

  return (
    <>
      <Card sx={{ padding: 0 }}>
        <CardContent>
          <Stack spacing={2}>
            <Box>
              <Typography variant="h4" color="primary" gutterBottom>
                Cliente
              </Typography>
              <Typography variant="body1" color="primary">
                Para quem você quer emitir?
              </Typography>
            </Box>

            <AutocompleteInput
              control={control}
              name="borrower"
              label="Selecionar cliente"
              noOptionsText={(() => {
                if (isLoading) {
                  return 'Buscando...'
                }

                if (debouncedSearchCustomer.trim().length > 2) {
                  return 'Cliente não encontrado! Clique no botão abaixo para cadastrar.'
                }

                return 'Busque pela Razão Social ou CPF/CNPJ...'
              })()}
              options={getBorrowerOptions(borrowersCache)}
              getOptionLabel={(option) =>
                option.key
                  ? `${option.name} - ${formatTaxId(option.tax_id)}`
                  : ''
              }
              getOptionKey={(option) => option.key}
              filterOptions={matchOptionsByTaxIdOrName}
              loading={isLoading}
              onChange={(_, newValue) =>
                setValue(
                  'borrower',
                  newValue || {
                    key: '',
                    name: '',
                    tax_id: '',
                    tax_id_type: 'cpf',
                  },
                )
              }
              onInputChange={(_, newInputValue, reason) => {
                if (reason === 'input') {
                  onSearchCustomerChange(newInputValue)
                  return
                }

                if (reason === 'clear' || reason === 'reset') {
                  onSearchCustomerChange('')
                }
              }}
              textFieldProps={{
                fullWidth: true,
                placeholder: 'Busque pela Razão Social ou CPF/CNPJ ',
              }}
            />

            {borrower && borrowerKey ? (
              <BorrowerDetailsCard
                borrower={borrower}
                onEdit={handleEditBorrower}
                loading={isReadingBorrower}
              />
            ) : (
              <Box display="flex" justifyContent="center">
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={() => setIsBorrowerRegistrationDialogOpen(true)}
                  sx={{ mt: 2 }}
                >
                  CADASTRAR CLIENTE
                </Button>
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>

      <BorrowerRegistrationDialog
        open={isBorrowerRegistrationDialogOpen}
        onClose={() => setIsBorrowerRegistrationDialogOpen(false)}
        onSuccess={handleBorrowerRegistered}
        mode="create"
      />

      <BorrowerRegistrationDialog
        open={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        onSuccess={handleBorrowerUpdated}
        mode="edit"
        borrower={borrower || undefined}
      />
    </>
  )
}

type BorrowerApiSearch = {
  tax_id?: string
  name?: string
}

const resolveBorrowerApiSearch = (
  input: string,
  borrowersCache: TBorrower[],
): BorrowerApiSearch => {
  const trimmedInput = input.trim()
  const hasLetters = /[A-Za-zÀ-ÿ]/.test(trimmedInput)

  if (hasLetters) {
    if (trimmedInput.length > 2) {
      return { name: trimmedInput }
    }

    return {}
  }

  const cleanInput = sanitizeCnpj(trimmedInput)
  if (cleanInput.length > 2) {
    const hasLocalMatch = borrowersCache.some((borrower) =>
      sanitizeCnpj(borrower.tax_id).includes(cleanInput),
    )
    if (!hasLocalMatch) {
      return { tax_id: cleanInput }
    }
  }

  return {}
}

const isSameBorrowerApiSearch = (
  current: BorrowerApiSearch,
  next: BorrowerApiSearch,
) => current.tax_id === next.tax_id && current.name === next.name

const matchOptionsByTaxIdOrName = (
  options: TBorrower[],
  state: FilterOptionsState<TBorrower>,
): TBorrower[] => {
  const inputValue = state.inputValue.toLowerCase()
  const cleanTaxIdInput = sanitizeCnpj(state.inputValue)

  if (!inputValue) {
    return options
  }

  const filteredOptions = options.filter((option) => {
    const label = `${option.name} - ${formatTaxId(option.tax_id)}`.toLowerCase()
    if (label.includes(inputValue)) {
      return true
    }

    if (
      cleanTaxIdInput &&
      sanitizeCnpj(option.tax_id).includes(cleanTaxIdInput)
    ) {
      return true
    }

    return false
  })

  return filteredOptions
}
